import { FC, useState } from 'react';
import { CreateMyBankAccountCommand } from 'api/bank-account';
import { Dialog } from 'components/feedback/Dialog/Dialog';
import { GenericMessage } from 'components/feedback/Message';
import { InputText } from 'components/forms/InputText';
import { Button } from 'components/ui/Button';
import { Sizes } from 'enums/sizes';
import { toast } from 'react-toastify';
import { bankAccountsAPI } from 'services/swagger-api';

interface BankAccountCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isFirstAccount?: boolean;
}

export const BankAccountCreateDialog: FC<BankAccountCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
  isFirstAccount = false
}) => {
  const [formData, setFormData] = useState({ iban: 'FR', label: '', bic: '' });
  const [isCreating, setIsCreating] = useState(false);

  const resetAndClose = () => {
    setFormData({ iban: 'FR', label: '', bic: '' });
    onClose();
  };

  const handleCreate = async () => {
    if (!formData.iban || !formData.label) {
      toast.error("L'IBAN et le libellé sont requis");
      return;
    }
    try {
      setIsCreating(true);
      const cleanIban = formData.iban.replace(/\s/g, '');
      const command: CreateMyBankAccountCommand = {
        iban: cleanIban,
        label: formData.label,
        bic: formData.bic || undefined,
        isDefault: isFirstAccount
      };
      const result = await bankAccountsAPI.createMyBankAccount({
        createMyBankAccountCommand: command
      });
      if (result.isSuccess) {
        resetAndClose();
        onSuccess();
      } else {
        toast.error(result.errorMessage || "Erreur lors de l'ajout du compte");
      }
    } catch (err) {
      toast.error("Erreur lors de l'ajout du compte");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={resetAndClose} size={Sizes.md}>
      <h1 className="font-20 text-dark font-w700 text-center mb-4">
        Ajout d'un nouveau compte bancaire
      </h1>
      <div className="flex flex-col gap-6">
        {isFirstAccount && (
          <GenericMessage
            type="info"
            message="Ce compte sera défini comme compte par défaut. Vos commissions seront versées sur ce compte."
            dismissible={false}
          />
        )}
        <InputText
          label="Libellé"
          required
          type="text"
          value={formData.label}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          disabled={isCreating}
        />
        <InputText
          label="IBAN"
          required
          type="text"
          value={formData.iban}
          onChange={(e) => {
            const value = e.target.value.replace(/\s/g, '');
            const formattedValue = value.replace(/(.{4})/g, '$1  ').trim();
            setFormData({ ...formData, iban: formattedValue });
          }}
          placeholder="FR00 0000 0000 0000 0000 0000 000"
          disabled={isCreating}
        />
        <InputText
          label="BIC"
          type="text"
          value={formData.bic}
          onChange={(e) => setFormData({ ...formData, bic: e.target.value.toUpperCase() })}
          placeholder="BNPAFRPP"
          disabled={isCreating}
        />
        <div className="flex grow gap-4 justify-center">
          <Button
            size={Sizes.lg}
            outline
            onClick={resetAndClose}
            disabled={isCreating}
          >
            Annuler
          </Button>
          <Button
            color="primary"
            size={Sizes.lg}
            onClick={handleCreate}
            disabled={isCreating}
          >
            {isCreating ? 'Ajout en cours...' : 'Valider'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
